import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseOutlined, SettingsOutlined,
  AddOutlined, DeleteOutlineOutlined, DragIndicatorOutlined,
  ViewCarouselOutlined, ImageOutlined, WarningAmberOutlined,
  ExpandMoreOutlined, ExpandLessOutlined,
} from '@mui/icons-material';
import { PRIMARY, slugify, LOBBY_BREAKPOINTS } from '../constants';
import { InspSectionTitle, InspRow, InspInput, ToggleRow, selectStyle } from '../ui/primitives';
import { MediaField } from '../MediaField';

const CAROUSEL_COLOR = '#c2410c';

// ── InlineNameInput ───────────────────────────────────────────────────────────
function InlineNameInput({ value, placeholder, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const inputRef              = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== value) onChange(trimmed || value);
  };

  if (editing) {
    return (
      <Box
        component="input"
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        sx={{
          flex: 1, minWidth: 0, border: 'none', outline: 'none',
          background: 'rgba(194,65,12,0.07)', borderRadius: '4px',
          px: '4px', py: '1px',
          fontSize: '0.7rem', fontWeight: 600, color: CAROUSEL_COLOR,
          fontFamily: 'inherit', width: '100%',
        }}
        autoFocus
      />
    );
  }

  return (
    <Typography
      onClick={e => { e.stopPropagation(); setEditing(true); }}
      onMouseDown={e => e.stopPropagation()}
      title="Click to rename"
      sx={{
        flex: 1, minWidth: 0,
        fontSize: '0.7rem', fontWeight: 600,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        cursor: 'text', borderRadius: '3px', px: '2px',
        '&:hover': { background: alpha(CAROUSEL_COLOR, 0.07) },
      }}
    >
      {value || placeholder}
    </Typography>
  );
}

// ── Carousel slide list ───────────────────────────────────────────────────────
function CarouselSlidesSection({
  lobby, activeSlideIndex, onSelectSlide, onAddSlide, onDeleteSlide,
  onReorderSlides, onUpdateSlide, onUpdateSlideResponsive, breakpoint,
}) {
  const slides = lobby?.slides || [];
  const [dragSrc, setDragSrc]   = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [expanded, setExpanded] = useState({ [activeSlideIndex]: true });

  const bp        = breakpoint || 'desktop';
  const isDesktop = bp === 'desktop';
  const bpDef     = LOBBY_BREAKPOINTS.find(b => b.key === bp);

  useEffect(() => {
    setExpanded(prev => ({ ...prev, [activeSlideIndex]: true }));
  }, [activeSlideIndex]);

  const toggleExpanded = (idx, e) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const getSlideValues = (s) => {
    if (!s) return { bgType: 'image', bgUrl: '', hasCustomBg: false };
    const bpData = !isDesktop ? (s.responsive?.[bp] || {}) : {};
    return {
      bgType:     isDesktop ? (s.bgType || 'image') : (bpData.bgType ?? s.bgType ?? 'image'),
      bgUrl:      isDesktop ? (s.bgUrl  || '')       : (bpData.bgUrl  ?? s.bgUrl  ?? ''),
      hasCustomBg: !isDesktop && (bpData.bgType !== undefined || bpData.bgUrl !== undefined),
    };
  };

  const updBg = (idx, patch) => {
    const s = slides[idx]; if (!s) return;
    if (isDesktop) onUpdateSlide(idx, patch);
    else onUpdateSlideResponsive?.(idx, bp, patch);
  };

  const clearBpBg = (idx) => {
    const s = slides[idx]; if (isDesktop || !s) return;
    const rest = { ...(s.responsive?.[bp] || {}) };
    delete rest.bgType; delete rest.bgUrl;
    onUpdateSlide(idx, { responsive: { ...(s.responsive || {}), [bp]: rest } });
  };

  const handleDragStart = (e, idx) => { setDragSrc(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragEnter = (idx)    => { if (dragSrc !== null && dragSrc !== idx) setDragOver(idx); };
  const handleDragOver  = (e)      => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop      = (e)      => {
    e.preventDefault();
    if (dragSrc === null || dragOver === null || dragSrc === dragOver) { setDragSrc(null); setDragOver(null); return; }
    const reordered = [...slides];
    const [moved] = reordered.splice(dragSrc, 1);
    reordered.splice(dragOver, 0, moved);
    onReorderSlides(reordered.map((s, i) => ({ ...s, order: i })));
    onSelectSlide(dragOver < dragSrc ? dragOver : Math.max(0, dragOver - 1));
    setDragSrc(null); setDragOver(null);
  };
  const handleDragEnd = () => { setDragSrc(null); setDragOver(null); };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <InspSectionTitle label="Slides" color={CAROUSEL_COLOR} />
        <Tooltip title="Add slide" placement="left">
          <IconButton size="small" onClick={onAddSlide}
            sx={{ color: CAROUSEL_COLOR, mr: -0.5, '&:hover': { background: alpha(CAROUSEL_COLOR, 0.08) } }}>
            <AddOutlined sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', mb: 1.5 }}>
        {slides.length === 0 && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#b0bac8' }}>No slides yet — click + to add one.</Typography>
          </Box>
        )}

        {slides.map((s, idx) => {
          const isActive  = idx === activeSlideIndex;
          const isDragSrc = dragSrc === idx;
          const isDragOv  = dragOver === idx;
          const isOpen    = !!expanded[idx];
          const { bgType, bgUrl, hasCustomBg } = getSlideValues(s);

          return (
            <Box key={s.id} draggable
              onDragStart={e => handleDragStart(e, idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              sx={{
                borderRadius: '8px',
                border: `1.5px solid ${isActive ? CAROUSEL_COLOR : isDragOv ? alpha(CAROUSEL_COLOR, 0.4) : '#e4e9f0'}`,
                background: isActive ? alpha(CAROUSEL_COLOR, 0.04) : '#fff',
                opacity: isDragSrc ? 0.4 : 1, transition: 'all 0.12s',
                boxShadow: isActive ? `0 0 0 3px ${alpha(CAROUSEL_COLOR, 0.08)}` : 'none',
                overflow: 'hidden',
              }}
            >
              {/* ── Header row ── */}
              <Box onClick={() => onSelectSlide(idx)}
                sx={{ display: 'flex', alignItems: 'center', gap: '5px', px: '7px', py: '6px', cursor: 'pointer' }}
              >
                <Box sx={{ color: '#c4cdd8', display: 'flex', cursor: 'grab', flexShrink: 0 }}
                  onMouseDown={e => e.stopPropagation()}>
                  <DragIndicatorOutlined sx={{ fontSize: 13 }} />
                </Box>

                {/* Thumbnail */}
                <Box sx={{ width: 36, height: 22, borderRadius: '3px', flexShrink: 0, overflow: 'hidden',
                  border: '1px solid #e4e9f0', background: s.bgUrl ? 'transparent' : '#f0f4f8', position: 'relative' }}>
                  {s.bgUrl
                    ? <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${s.bgUrl})`,
                        backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    : <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageOutlined sx={{ fontSize: 11, color: '#c4cdd8' }} />
                      </Box>
                  }
                  <Box sx={{ position: 'absolute', top: 1, left: 2, fontSize: '0.45rem', fontWeight: 700,
                    color: '#fff', lineHeight: 1, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}>{idx + 1}</Box>
                </Box>

                {/* Inline name */}
                <Box sx={{ flex: 1, minWidth: 0, color: isActive ? CAROUSEL_COLOR : '#3c4555' }}>
                  <InlineNameInput
                    value={s.label || `Slide ${idx + 1}`}
                    placeholder={`Slide ${idx + 1}`}
                    onChange={v => onUpdateSlide(idx, { label: v })}
                  />
                </Box>

                {/* Expand/collapse */}
                <Box onClick={e => toggleExpanded(idx, e)}
                  sx={{ color: '#c4cdd8', display: 'flex', cursor: 'pointer', flexShrink: 0,
                    '&:hover': { color: CAROUSEL_COLOR } }}>
                  {isOpen ? <ExpandLessOutlined sx={{ fontSize: 15 }} /> : <ExpandMoreOutlined sx={{ fontSize: 15 }} />}
                </Box>

                {/* Delete */}
                {slides.length > 1 && (
                  <IconButton size="small" onClick={e => { e.stopPropagation(); onDeleteSlide(idx); }}
                    sx={{ color: '#c4cdd8', flexShrink: 0, p: '2px',
                      '&:hover': { color: '#dc2626', background: alpha('#dc2626', 0.06) } }}>
                    <DeleteOutlineOutlined sx={{ fontSize: 13 }} />
                  </IconButton>
                )}
              </Box>

              {/* ── Expanded: type + file ── */}
              {isOpen && (
                <Box sx={{ px: '10px', pb: '8px', display: 'flex', flexDirection: 'column', gap: '5px',
                  borderTop: `1px solid ${isActive ? alpha(CAROUSEL_COLOR, 0.15) : '#f0f4f8'}` }}>

                  {!isDesktop && (
                    <Box sx={{ mt: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                      background: alpha(bpDef.color, 0.08), border: `1px dashed ${alpha(bpDef.color, 0.45)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ color: bpDef.color, display: 'flex', fontSize: 12 }}>{bpDef.icon}</Box>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: bpDef.color }}>{bpDef.label} override</Typography>
                      </Box>
                      {hasCustomBg && (
                        <Box onClick={() => clearBpBg(idx)}
                          sx={{ fontSize: '0.58rem', fontWeight: 600, color: bpDef.color,
                            cursor: 'pointer', opacity: 0.75, '&:hover': { opacity: 1 } }}>Reset</Box>
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', pt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#9ea8b3', width: 32, flexShrink: 0 }}>Type</Typography>
                    <select value={bgType} onChange={e => updBg(idx, { bgType: e.target.value })} style={selectStyle}>
                      <option value="image">Image</option>
                      <option value="gif">GIF</option>
                      <option value="video">Video</option>
                    </select>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#9ea8b3', width: 32, flexShrink: 0 }}>File</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <MediaField
                        value={bgUrl}
                        onChange={url => updBg(idx, { bgUrl: url || null })}
                        mediaType={bgType === 'video' ? 'video' : 'image'}
                      />
                    </Box>
                  </Box>

                  {/* Hide navigation toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', pt: 0.25 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#9ea8b3', width: 32, flexShrink: 0 }}>Nav</Typography>
                    <Box
                      onClick={() => onUpdateSlide(idx, { hideNav: !s.hideNav })}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        cursor: 'pointer', userSelect: 'none',
                        px: 0.75, py: 0.4, borderRadius: '6px',
                        border: `1px solid ${s.hideNav ? '#fca5a5' : '#e4e9f0'}`,
                        background: s.hideNav ? '#fff5f5' : '#f8f9fb',
                        transition: 'all 0.13s',
                        '&:hover': { borderColor: s.hideNav ? '#f87171' : '#c8d4df' },
                      }}
                    >
                      {/* Mini toggle */}
                      <Box sx={{
                        width: 24, height: 13, borderRadius: '7px', flexShrink: 0, position: 'relative',
                        background: s.hideNav ? '#fca5a5' : '#d1d5db',
                        transition: 'background 0.15s',
                      }}>
                        <Box sx={{
                          position: 'absolute', top: 1.5, left: s.hideNav ? 12 : 1.5,
                          width: 10, height: 10, borderRadius: '50%',
                          background: s.hideNav ? '#dc2626' : '#9ca3af',
                          transition: 'left 0.15s',
                        }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: s.hideNav ? '#dc2626' : '#9ea8b3' }}>
                        {s.hideNav ? 'Hidden' : 'Visible'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </>
  );
}

// ── RemoveCarouselButton ──────────────────────────────────────────────────────
function RemoveCarouselButton({ onRemoveCarousel }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <>
      <Box
        onClick={() => setConfirm(true)}
        sx={{
          mt: 1.5, px: 1.25, py: 0.9, borderRadius: '8px',
          border: '1px solid #fee2e2', background: '#fff5f5',
          display: 'flex', alignItems: 'center', gap: 0.75,
          cursor: 'pointer', transition: 'all 0.13s',
          '&:hover': { borderColor: '#fca5a5', background: '#fee2e2' },
        }}
      >
        <Box sx={{ width: 18, height: 18, borderRadius: '4px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ViewCarouselOutlined sx={{ fontSize: 11, color: '#dc2626' }} />
        </Box>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626' }}>
          Remove Exhibitor Carousel
        </Typography>
      </Box>

      {confirm && (
        <Box onClick={() => setConfirm(false)}
          sx={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box onClick={e => e.stopPropagation()}
            sx={{ background: '#fff', borderRadius: '14px', boxShadow: '0 16px 48px rgba(0,0,0,0.22)', p: 3, width: 340, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: '#fff5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <WarningAmberOutlined sx={{ fontSize: 22, color: '#dc2626' }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>Remove Exhibitor Carousel?</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: '#5a6474', lineHeight: 1.6 }}>
              Removing the exhibitor carousel will <strong>permanently delete all slide contents</strong> including backgrounds, hotspots, and elements across all slides. This cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Box onClick={() => setConfirm(false)}
                sx={{ flex: 1, py: 0.9, borderRadius: '8px', border: '1px solid #e4e9f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#5a6474', fontSize: '0.78rem', fontWeight: 600, '&:hover': { borderColor: '#c8d4df', background: '#f8f9fb' } }}>
                Cancel
              </Box>
              <Box onClick={() => { setConfirm(false); onRemoveCarousel(); }}
                sx={{ flex: 1.4, py: 0.9, borderRadius: '8px', background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '0.78rem', fontWeight: 700, '&:hover': { background: '#b91c1c' } }}>
                Remove Carousel
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

// ── CanvasInspector ───────────────────────────────────────────────────────────
export function CanvasInspector({
  lobby,
  onUpdateLobby,
  onUpdateLobbyResponsive,
  onClose,
  breakpoint,
  // Carousel-specific props (passed through from LobbyBuilderTab)
  activeSlideIndex,
  onSelectSlide,
  onAddSlide,
  onDeleteSlide,
  onReorderSlides,
  onUpdateSlide,
  onUpdateSlideResponsive,
  onRemoveCarousel,
  onConvertToCarousel,
}) {
  if (!lobby) return null;

  const isCarousel = lobby.lobbyType === 'carousel';
  const upd        = patch => onUpdateLobby(patch);

  const bp        = breakpoint || 'desktop';
  const isDesktop = bp === 'desktop';
  const bpDef     = LOBBY_BREAKPOINTS.find(b => b.key === bp);

  // ── Active source: for carousel = active slide, otherwise = lobby itself ──
  const activeSlide  = isCarousel ? (lobby.slides?.[activeSlideIndex ?? 0] ?? null) : null;
  const source       = isCarousel ? activeSlide : lobby;

  // Bg derivation
  const bpData      = !isDesktop ? ((source?.responsive?.[bp]) || {}) : {};
  const activeBgType = isDesktop ? (source?.bgType || 'image') : (bpData.bgType ?? source?.bgType ?? 'image');
  const activeBgUrl  = isDesktop ? (source?.bgUrl  || '')      : (bpData.bgUrl  ?? source?.bgUrl  ?? '');
  const hasCustomBg  = !isDesktop && (bpData.bgType !== undefined || bpData.bgUrl !== undefined);

  const updBg = (patch) => {
    if (isCarousel) {
      if (isDesktop) onUpdateSlide?.(activeSlideIndex ?? 0, patch);
      else onUpdateSlideResponsive?.(activeSlideIndex ?? 0, bp, patch);
    } else {
      if (isDesktop) upd(patch);
      else onUpdateLobbyResponsive?.(bp, patch);
    }
  };

  const clearBpBg = () => {
    if (isDesktop) return;
    if (isCarousel) {
      const s = activeSlide;
      if (!s) return;
      const rest = { ...(s.responsive?.[bp] || {}) };
      delete rest.bgType; delete rest.bgUrl;
      onUpdateSlide?.(activeSlideIndex ?? 0, { responsive: { ...(s.responsive || {}), [bp]: rest } });
    } else {
      const rest = { ...(lobby.responsive?.[bp] || {}) };
      delete rest.bgType; delete rest.bgUrl;
      onUpdateLobby({ responsive: { ...(lobby.responsive || {}), [bp]: rest } });
    }
  };

  // Intro / nav — read from slide when carousel, lobby otherwise
  const introVideoUrl = isCarousel ? (activeSlide?.introVideoUrl || '') : (lobby.introVideoUrl || '');
  const showIntro     = isCarousel ? (activeSlide?.showIntro !== false) : (lobby.showIntro !== false);
  const hideNav       = isCarousel ? (!!activeSlide?.hideNav) : false;

  const updIntroVideo = v => isCarousel ? onUpdateSlide?.(activeSlideIndex ?? 0, { introVideoUrl: v }) : upd({ introVideoUrl: v });
  const updShowIntro  = v => isCarousel ? onUpdateSlide?.(activeSlideIndex ?? 0, { showIntro: v })     : upd({ showIntro: v });
  const updHideNav    = v => isCarousel ? onUpdateSlide?.(activeSlideIndex ?? 0, { hideNav: !v })      : undefined;

  const slideLabel = isCarousel && activeSlide ? (activeSlide.label || `Slide ${(activeSlideIndex ?? 0) + 1}`) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ px: 2, py: 1.75, borderBottom: '1px solid #e8edf2', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 26, height: 26, borderRadius: 1.5, background: alpha(isCarousel ? CAROUSEL_COLOR : PRIMARY, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: isCarousel ? CAROUSEL_COLOR : PRIMARY }}>
            {isCarousel ? <ViewCarouselOutlined sx={{ fontSize: 15 }} /> : <SettingsOutlined sx={{ fontSize: 15 }} />}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1a1a2e', lineHeight: 1.2 }}>Lobby Settings</Typography>
            {slideLabel && (
              <Typography sx={{ fontSize: '0.62rem', color: CAROUSEL_COLOR, fontWeight: 600, lineHeight: 1.2 }}>{slideLabel}</Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#9ea8b3' }}>
          <CloseOutlined sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>

        {/* ── Lobby type toggle ── */}
        <Box sx={{ display: 'flex', gap: '6px', mb: 2 }}>
          {[
            { key: 'lobby',    label: 'Single',   icon: <SettingsOutlined sx={{ fontSize: 14 }} />,        color: PRIMARY,        desc: 'One background' },
            { key: 'carousel', label: 'Carousel', icon: <ViewCarouselOutlined sx={{ fontSize: 14 }} />,    color: CAROUSEL_COLOR, desc: 'Multiple slides' },
          ].map(opt => {
            const isActive = isCarousel ? opt.key === 'carousel' : opt.key === 'lobby';
            return (
              <Box
                key={opt.key}
                onClick={() => {
                  if (isActive) return;
                  if (opt.key === 'carousel') onConvertToCarousel?.();
                  else onRemoveCarousel?.();
                }}
                sx={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
                  px: 0.75, py: 0.85, borderRadius: '8px', cursor: isActive ? 'default' : 'pointer',
                  border: `1.5px solid ${isActive ? opt.color : '#e4e9f0'}`,
                  background: isActive ? alpha(opt.color, 0.06) : '#f8f9fb',
                  transition: 'all 0.13s',
                  '&:hover': isActive ? {} : { borderColor: alpha(opt.color, 0.5), background: alpha(opt.color, 0.04) },
                }}
              >
                <Box sx={{ color: isActive ? opt.color : '#b0bac8', display: 'flex' }}>{opt.icon}</Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: isActive ? opt.color : '#9ea8b3', lineHeight: 1 }}>{opt.label}</Typography>
                <Typography sx={{ fontSize: '0.56rem', color: isActive ? alpha(opt.color, 0.7) : '#c4cdd8', lineHeight: 1 }}>{opt.desc}</Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── Carousel-only controls: Add Slide + Navigation ── */}
        {isCarousel && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mb: 1.5 }}>
            <Box
              onClick={onAddSlide}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                py: 0.75, borderRadius: '8px', cursor: 'pointer',
                border: `1.5px dashed ${alpha(CAROUSEL_COLOR, 0.5)}`,
                background: alpha(CAROUSEL_COLOR, 0.04),
                transition: 'all 0.13s',
                '&:hover': { background: alpha(CAROUSEL_COLOR, 0.1), borderColor: CAROUSEL_COLOR },
              }}
            >
              <AddOutlined sx={{ fontSize: 14, color: CAROUSEL_COLOR }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: CAROUSEL_COLOR }}>Add Slide</Typography>
            </Box>
            <ToggleRow label="Navigation visible" value={!hideNav} onChange={v => updHideNav(v)} />
          </Box>
        )}

        {/* ── Background ── */}
        <InspSectionTitle label="Background" color={PRIMARY} />

        {!isDesktop && (
          <Box sx={{
            mb: 1.25, px: 1.25, py: 0.75, borderRadius: 1.5,
            background: alpha(bpDef.color, 0.08), border: `1px dashed ${alpha(bpDef.color, 0.45)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ color: bpDef.color, display: 'flex', alignItems: 'center' }}>{bpDef.icon}</Box>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: bpDef.color }}>{bpDef.label} override</Typography>
            </Box>
            {hasCustomBg && (
              <Box onClick={clearBpBg} sx={{ fontSize: '0.62rem', fontWeight: 600, color: bpDef.color, cursor: 'pointer', opacity: 0.75, '&:hover': { opacity: 1 } }}>
                Reset to Desktop
              </Box>
            )}
          </Box>
        )}

        <InspRow label="Type">
          <select value={activeBgType} onChange={e => updBg({ bgType: e.target.value })} style={selectStyle}>
            <option value="image">Image</option>
            <option value="gif">GIF</option>
            <option value="video">Video</option>
          </select>
        </InspRow>
        <InspRow label="File">
          <MediaField
            value={activeBgUrl}
            onChange={v => updBg({ bgUrl: v || null })}
            mediaType={activeBgType === 'video' || activeBgType === 'gif' ? 'video' : 'image'}
          />
        </InspRow>

        {isDesktop && (
          <>
            <InspSectionTitle label="Videos" color="#8b4fcc" />
            <InspRow label="Intro Video">
              <MediaField value={introVideoUrl} onChange={v => updIntroVideo(v)} mediaType="video" />
            </InspRow>
            <InspSectionTitle label="Intro Modal" color="#00a882" />
            <ToggleRow label="Show intro modal" value={showIntro} onChange={v => updShowIntro(v)} />
          </>
        )}
      </Box>
    </Box>
  );
}